import { Box, Card, Divider, Flex, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import isNil from "lodash/isNil";
import { useCallback, useState } from "react";

import { useCreateLimitRequestPaymaster } from "../../../api/hooks/limitRequest/paymaster/useCreateLimitRequestPaymaster.ts";
import { useDeleteLimitRequestPaymaster } from "../../../api/hooks/limitRequest/paymaster/useDeleteLimitRequestPaymaster.ts";
import { useLimitRequestPaymasters } from "../../../api/hooks/limitRequest/paymaster/useLimitRequestPaymasters.ts";
import { useUpdateLimitRequestPaymaster } from "../../../api/hooks/limitRequest/paymaster/useUpdateLimitRequestPaymaster.ts";
import { useCreatePaymaster } from "../../../api/hooks/paymaster/useCreatePaymaster.ts";
import { usePaymasters } from "../../../api/hooks/paymaster/usePaymasters.ts";
import { Loader } from "../../../components/Loader.tsx";
import Table from "../../../components/Table/Table.tsx";
import {
  CreateLimitRequestPaymasterInput,
  OrderBy,
  PaymasterStatusFilterOptions,
} from "../../../graphql/generated.ts";
import { useMutationNotificationWrapper } from "../../../hooks/useMutationNotificationWrapper.tsx";
import { LimitRequestPaymaster } from "../../../types/limitRequest/paymaster.ts";
import { useLimitRequestPaymastersTableData } from "../hooks/useLimitRequestPaymastersTableData.tsx";
import { CreateLimitRequestPaymasterModal } from "./CreateLimitRequestPaymasterModal.tsx";
import { useStyles } from "./LimitRequestPaymasters.styles.tsx";

interface Props {
  customerId: number | undefined;
  limitRequestId: number | undefined;
}

export const LimitRequestPaymasters = ({
  customerId,
  limitRequestId,
}: Props) => {
  const { classes } = useStyles({ variant: "blue" });

  const [
    createModalOpened,
    { open: openCreateModal, close: closeCreateModal },
  ] = useDisclosure(false);
  const [selectedPaymaster, setSelectedPaymaster] =
    useState<LimitRequestPaymaster>();

  const [
    { data: paymastersData, fetching: fetchingPaymasters },
    refreshPaymasters,
  ] = usePaymasters({
    filter: {
      status: { equals: PaymasterStatusFilterOptions.Active },
    },
    orderBy: { name: OrderBy.Asc },
    pause: isNil(customerId),
  });
  const paymasters = paymastersData?.paymasters.data;

  const [
    {
      data: limitRequestPaymastersData,
      fetching: fetchingLimitRequestPaymasters,
    },
    refresh,
  ] = useLimitRequestPaymasters({
    filter: {
      limitRequestId: { equals: limitRequestId },
      paymaster: {
        status: { equals: PaymasterStatusFilterOptions.Active },
      },
    },
    orderBy: { id: OrderBy.Asc },
    pause: isNil(limitRequestId),
  });
  const limitRequestPaymasters =
    limitRequestPaymastersData?.limitRequestPaymasters;

  const loading = fetchingPaymasters || fetchingLimitRequestPaymasters;

  const [, createPaymaster] = useMutationNotificationWrapper(
    useCreatePaymaster(),
    {
      success: {
        message: "Paymaster has been successfully created.",
      },
    }
  );

  const [, createLimitRequestPaymaster] = useMutationNotificationWrapper(
    useCreateLimitRequestPaymaster(),
    {
      success: {
        message: "Limit Request Paymaster has been successfully created.",
      },
    }
  );
  const [, updateLimitRequestPaymaster] = useMutationNotificationWrapper(
    useUpdateLimitRequestPaymaster(),
    {
      success: {
        message: "Limit Request Paymaster has been successfully updated.",
      },
    }
  );
  const [, deletePaymaster] = useMutationNotificationWrapper(
    useDeleteLimitRequestPaymaster(),
    {
      success: {
        message: "Limit Request Paymaster has been successfully deleted.",
      },
    }
  );

  const handleCloseCreateModal = useCallback(() => {
    closeCreateModal();
    setSelectedPaymaster(undefined);
  }, [closeCreateModal]);

  const handleCreatePaymaster = useCallback(
    (name: string) => {
      if (!customerId) {
        return;
      }
      createPaymaster({
        input: { name },
      }).then(refreshPaymasters);
      return undefined;
    },
    [customerId, createPaymaster, refreshPaymasters]
  );

  const handleCreate = useCallback(
    (input: Omit<CreateLimitRequestPaymasterInput, "limitRequestId">) => {
      if (selectedPaymaster) {
        updateLimitRequestPaymaster({
          id: selectedPaymaster.id,
          input: {
            status: input.status,
            notes: input.notes,
          },
        })
          .then(handleCloseCreateModal)
          .finally(refresh);
        return;
      }
      if (!limitRequestId) {
        return;
      }
      createLimitRequestPaymaster({ input: { ...input, limitRequestId } })
        .then(handleCloseCreateModal)
        .finally(refresh);
    },
    [
      createLimitRequestPaymaster,
      handleCloseCreateModal,
      limitRequestId,
      refresh,
      selectedPaymaster,
      updateLimitRequestPaymaster,
    ]
  );

  const handleUpdate = useCallback(
    (id: number) => {
      const paymaster = limitRequestPaymasters?.find(
        (paymaster) => paymaster.id === id
      );
      if (!paymaster) {
        return;
      }
      setSelectedPaymaster(paymaster);
      openCreateModal();
    },
    [limitRequestPaymasters, openCreateModal]
  );

  const handleDelete = useCallback(
    (id: number) => {
      deletePaymaster({ id }).then(refresh);
    },
    [deletePaymaster, refresh]
  );

  const [columns, rows] = useLimitRequestPaymastersTableData({
    data: limitRequestPaymasters,
    onEdit: handleUpdate,
    onDelete: handleDelete,
  });

  return (
    <>
      <Card padding={20} radius={4} className={classes.wrapper}>
        <Flex direction="column" h="100%" gap={12} pos="relative">
          <Flex justify="space-between">
            <Flex gap={12} align="center">
              <Box className={classes.pin} />
              <Text className={classes.label}>Paymasters Data</Text>
            </Flex>
            <Text className={classes.addText} onClick={openCreateModal}>
              Add
            </Text>
          </Flex>
          <Divider />
          {loading && <Loader mih="100%" />}
          <Table
            loading={loading}
            borderColor="transparent"
            rowBorderColor="transparent"
            minWidth="100%"
            mih={0}
            h="100%"
            columns={columns}
            rows={rows}
          />
        </Flex>
      </Card>
      {createModalOpened && (
        <CreateLimitRequestPaymasterModal
          key={paymasters?.length ?? 0}
          data={selectedPaymaster}
          paymasters={paymasters || []}
          opened={true}
          onClose={handleCloseCreateModal}
          onSubmit={handleCreate}
          onCreatePaymaster={handleCreatePaymaster}
        />
      )}
    </>
  );
};
